/**
 * Pure helpers for `TruckDetails.tsx`, kept dependency-free (no React/MUI/store
 * imports) so they can be unit tested directly in this repo's Node-only vitest
 * environment without needing a DOM.
 */

/** Query-param values for the two Truck Details tabs. Task 7 renders the `schedule` panel. */
export type TruckDetailsTab = 'details' | 'schedule';

/** Resolves the `?tab=` search param to a known tab, defaulting to `details`. */
export function resolveTruckDetailsTab(tabParam: string | null): TruckDetailsTab {
  return tabParam === 'schedule' ? 'schedule' : 'details';
}

export type TruckDetailsViewState = 'no-organization' | 'loading' | 'not-found' | 'error' | 'ready';

/**
 * Picks which of the page's early-return states applies, mirroring
 * `SupplierDetails.tsx`'s inline branching:
 *   1. no organization selected
 *   2. loading, and we don't already have the matching truck loaded
 *   3. the store flagged a 404 (not found)
 *   4. any other case where the loaded truck doesn't match the route param (error)
 *   5. otherwise ready to render
 */
export function resolveTruckDetailsViewState(args: {
  hasOrganization: boolean;
  isLoading: boolean;
  truckMatchesParam: boolean;
  notFound: boolean;
}): TruckDetailsViewState {
  const { hasOrganization, isLoading, truckMatchesParam, notFound } = args;
  if (!hasOrganization) return 'no-organization';
  if (isLoading && !truckMatchesParam) return 'loading';
  if (notFound) return 'not-found';
  if (!truckMatchesParam) return 'error';
  return 'ready';
}
